'use client';

import { createContext, useContext } from 'react';
import type { PageContainerLayout } from './PageContainer';

/** State for the panel */
export interface PageContainerPanelState {
  isOpen: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** All user-facing strings rendered by PageContainer slots. All keys are optional — unspecified keys use built-in English defaults. */
export interface PageContainerText {
  /** Accessible label for the side panel region (`role="complementary"`). Default: `"Side panel"`. */
  panelLabel?: string;
  /** Accessible label for the panel resize handle. Default: `"Resize panel"`. */
  resizePanelLabel?: string;
  /** Accessible label for the close panel button. Default: `"Close panel"`. */
  closePanelLabel?: string;
  /** Accessible label for the toggle panel button. Default: `"Toggle panel"`. */
  togglePanelLabel?: string;
  /** Accessible label for the breadcrumb `<nav>` element. Default: `"Breadcrumb"`. */
  breadcrumbNavLabel?: string;
  /** Accessible label for the button that opens the breadcrumbs collapsed for space. Default: `"View more pages"`. */
  breadcrumbCollapsedLabel?: string;
  /**
   * `aria-label` for the `<header>` that wraps the page title, badge, description, and actions.
   * Omit on most pages — a `<header>` nested inside `<section>` carries no landmark role and needs no
   * label. Supply this only when a route renders **multiple** `<PageContainer>` instances side-by-side
   * and assistive technology must differentiate their heading areas (e.g. `"Main content"` vs
   * `"Secondary content"`).
   */
  headingLabel?: string;
  /**
   * `aria-label` for the `<section>` that holds the page content. Naming it makes the section a
   * `region` landmark, so supply it only when screen reader users need to jump to the content
   * area from a panel beside it (e.g. `"Chat"`). No default: an unnamed section is not a landmark.
   */
  contentLabel?: string;
}

type OptionalTextKeys = 'headingLabel' | 'contentLabel' | 'breadcrumbCollapsedLabel';
type ResolvedPageContainerText = Required<Omit<PageContainerText, OptionalTextKeys>> & Pick<PageContainerText, 'headingLabel'>;

/** Main context value - tracks panel state and any future shared state */
export interface PageContainerContextValue {
  panel: PageContainerPanelState | null;
  /** Stable ID applied to the panel element. Used by button slots for `aria-controls`. */
  panelId: string;
  /** Whether the scrollable content area has been scrolled from the top */
  scrolled: boolean;
  /** Resolved text strings for all panel slots. */
  text: ResolvedPageContainerText;
  /**
   * True when the panel is open and the container is too narrow for side-by-side layout,
   * so the panel overlays the main content. Used to apply `aria-modal` on the panel and
   * `inert` on the main content.
   */
  isOverlayPanel: boolean;
  /** Current layout mode — consumed by slot renderDefaults that need to branch on layout. */
  layout: PageContainerLayout;
  /**
   * Attach to a scrollable element to drive the header shadow and `scrolled` state.
   *
   * **Default:** already attached to PageContainer's internal scroll div — nothing to do for
   * standard pages.
   *
   * **Override:** on `layout="fixed-height"` pages an inner component (e.g. a chat thread) owns
   * its own scroll container. Call `usePageContainerScrollRef()` inside that component and attach
   * the returned ref to the scrollable element instead.
   */
  scrollRef: (node: HTMLElement | null) => void;
  /**
   * The current scroll container element. In `layout="chat"` mode this is the
   * `page-content-container` div managed by PageContainer. In other modes it is
   * whatever element `scrollRef` was last attached to.
   */
  scrollElement: HTMLElement | null;
}

export const PageContainerContext = createContext<PageContainerContextValue | null>(null);

/** Returns the full PageContainer context. Throws when called outside a `<PageContainer>`. */
export function usePageContainerContext(): PageContainerContextValue {
  const ctx = useContext(PageContainerContext);
  if (!ctx) throw new Error('usePageContainerContext must be used within a PageContainer');
  return ctx;
}

/** Hook to get the resolved text strings for all panel slots. */
export function usePageContainerText(): ResolvedPageContainerText {
  return usePageContainerContext().text;
}

/** Hook to get the stable ID of the panel element, for use in `aria-controls`. */
export function usePageContainerPanelId(): string {
  return usePageContainerContext().panelId;
}

/** Hook to get panel control state. */
export function usePageContainerPanel(): PageContainerPanelState {
  const { panel } = usePageContainerContext();
  return panel ?? { isOpen: false, onOpenChange: undefined };
}

/** Returns whether the PageContainer's scrollable content area has been scrolled from the top. */
export function useIsPageContainerScrolled(): boolean {
  return usePageContainerContext().scrolled;
}

/** Returns the current layout mode of the nearest PageContainer. */
export function usePageContainerLayout(): PageContainerLayout {
  return usePageContainerContext().layout;
}

/**
 * Returns the scroll ref callback from the nearest `<PageContainer>`.
 *
 * **Standard pages:** the ref is already attached to PageContainer's internal scroll div — no action needed.
 *
 * **`layout="fixed-height"` pages:** if a child component (e.g. a chat thread) owns its own
 * scrollable element, call this hook inside that child and attach the returned ref so the header
 * shadow and `scrolled` state track the child's scroll position instead of the (non-scrolling)
 * PageContainer root:
 *
 * ```tsx
 * function ChatThread() {
 *   const scrollRef = usePageContainerScrollRef();
 *   return <div ref={scrollRef} className="overflow-y-auto flex-1 min-h-0">...</div>;
 * }
 * ```
 *
 * The ref callback is stable across renders (created once by `useScroll` inside `PageContainerBase`).
 */
export function usePageContainerScrollRef(): (node: HTMLElement | null) => void {
  return usePageContainerContext().scrollRef;
}

/** Provider component for PageContainer context */
export interface PageContainerProviderProps {
  children: React.ReactNode;
  value: PageContainerContextValue;
}

export function PageContainerProvider({ children, value }: PageContainerProviderProps) {
  return <PageContainerContext.Provider value={value}>{children}</PageContainerContext.Provider>;
}
